<template>
  <!-- CD-12 ACCOUNT PAYMENT DETAILS -->
  <div class="space-y-6">
    <div v-if="isLoading" class="flex justify-center items-center py-12">
      <LayupSpinner :size="12" container-classes="" />
    </div>
    <template v-else>
      <div class="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-8">
        <div>
          <h3 class="text-[14px] font-semibold mb-1 text-grey-light-700">Payment Methods</h3>
          <p class="text-[14px] text-grey-light-600">Add card details.</p>
        </div>
        <div class="space-y-4">
          <ManageCards
            :cards="cards"
            :loading-card-id="loadingCardId"
            @set-default="setAsDefault"
            @remove-card="removeCard"
            @card-added="onCardAdded"
          />
        </div>
      </div>

      <hr class="col-span-full mb-[50px]" />

      <div class="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-8">
        <div>
          <h3 class="text-[14px] font-semibold mb-1 text-grey-light-700">Banking details</h3>
          <p class="text-[14px] text-grey-light-600">Add bank details.</p>
          <p class="text-xs text-grey-light-500 mt-2">
            Please note that you can not add bank details between 8:30pm and 6am.
          </p>
        </div>
        <div v-if="!hasBankDetails" class="space-y-4">
          <p class="text-grey-light-600">No Bank Details Added. Please add your bank details.</p>
          <div class="flex md:max-w-[512px]" v-if="!hasIdentity">
            <p class="text-sm text-warning-500">
              Please add your ID or passport number in Personal Details before adding bank details.
            </p>
          </div>
          <div class="flex md:max-w-[512px]" :class="isDesktop ? 'justify-end' : 'justify-start'">
            <Button
              variant="link"
              class="text-brand-700 font-semibold px-0"
              :disabled="!hasIdentity"
              @click="addBankDetails"
              data-testid="add-details-button"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" class="mr-2">
                <path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="2" />
              </svg>
              Add Details
            </Button>
          </div>
        </div>
        <div v-else>
          <AddBankDetails
            ref="bankDetailsRef"
            v-model="bankDetails"
            :banks="banks"
            :is-editing="editingBank"
            :is-loading="isSaving"
          />
          <div v-if="!editingBank" class="flex md:max-w-[512px] justify-start md:justify-end mt-2">
            <Button variant="outline" class="w-full md:w-auto" @click="editingBank = true">Edit</Button>
          </div>
        </div>
      </div>

      <hr class="col-span-full mb-[50px]" v-if="isDesktop && hasBankDetails && editingBank" />
      <div v-if="hasBankDetails && editingBank" class="flex flex-col-reverse md:flex-row justify-end gap-3">
        <Button
          variant="outline"
          class="w-full md:w-auto"
          :disabled="isSaving"
          @click="handleCancel"
          data-testid="cancel-button"
          >Cancel</Button
        >
        <Button
          class="bg-brand-600 hover:bg-brand-700 w-full md:w-auto"
          :disabled="isSaving || !editingBank"
          @click="submitBankDetails"
          data-testid="save-button"
        >
          <span v-if="isSaving" class="flex items-center gap-2">
            <LayupSpinner :size="4" container-classes="" />
            Saving...
          </span>
          <span v-else>Save</span>
        </Button>
      </div>

      <ConfirmDialog
        :open="showSetDefaultDialog"
        title="Set as Default Card?"
        max-width="sm:max-w-[500px]"
        :prevent-close="true"
      >
        <p class="text-sm text-grey-light-600 mb-2 text-center">
          Would you like to set this new card ending with {{ newlyAddedCardDetails?.cardDigits }} as your default
          payment method?
        </p>
        <SectionDivider className="mb-1" />
        <div class="flex justify-end gap-3">
          <Button variant="outline" @click="skipSetDefault" :disabled="loadingCardId !== null">No</Button>
          <Button
            @click="setAsDefault(newlyAddedCardDetails?._id!, true)"
            class="bg-brand-600 hover:bg-brand-700 max-w-[55px]"
            :disabled="loadingCardId !== null"
          >
            <span v-if="loadingCardId" class="flex items-center gap-2">
              <LayupSpinner :size="4" container-classes="" />
            </span>
            <span v-else>Yes</span>
          </Button>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        :open="showSelectDefaultDialog"
        title="Select New Default Card"
        max-width="sm:max-w-[600px]"
        :prevent-close="false"
        @update:open="showSelectDefaultDialog = false"
      >
        <p class="text-sm text-grey-light-600 mb-2 text-center">
          Please select a new default card before deleting the current default card.
        </p>
        <SectionDivider className="mb-1" />
        <div class="space-y-2">
          <div
            v-for="card in cards.filter((c) => c._id !== cardToDelete)"
            :key="card._id"
            class="border rounded-lg p-4 flex items-center justify-between relative"
            :class="loadingCardId === card._id ? 'border-brand-300' : 'border-gray-200'"
          >
            <div
              v-if="loadingCardId === card._id"
              class="absolute inset-0 bg-white/80 rounded-lg flex items-center justify-center z-10"
            >
              <LayupSpinner :size="8" container-classes="" />
            </div>
            <div class="flex items-center gap-3">
              <div class="w-[46px] h-[32px] flex items-center justify-center">
                <component :is="checkCardBrand(card).icon" />
              </div>
              <div>
                <p class="text-[14px] text-brand-800 font-medium">{{ checkCardBrand(card).customDescription }}</p>
                <p class="text-[14px] text-brand-600">Expiry {{ card.expiryMonth }}/{{ card.expiryYear }}</p>
              </div>
            </div>
            <Button @click="setDefaultAndDelete(card._id!)" class="bg-brand-600 hover:bg-brand-700 w-[100px]"
              >Set Default</Button
            >
          </div>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        :open="showVerifyingDialog"
        title="Verifying Bank Details"
        max-width="sm:max-w-[400px]"
        :prevent-close="true"
      >
        <div class="flex flex-col items-center py-6">
          <LayupBrandedSpinner />
          <p class="text-sm text-grey-light-600 mt-4">Please wait while we verify your bank details...</p>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        :open="showDeleteCardDialog"
        title="Delete Card"
        max-width="sm:max-w-[500px]"
        :prevent-close="false"
        @update:open="showDeleteCardDialog = false"
      >
        <p class="text-sm text-grey-light-600 text-center">
          Are you sure you want to delete card ending with {{ cards.find((c) => c._id === cardToDelete)?.cardDigits }}?
        </p>
        <SectionDivider className="mb-1" />
        <div class="flex justify-end gap-3">
          <Button variant="outline" @click="showDeleteCardDialog = false">Cancel</Button>
          <Button @click="confirmDeleteCard" class="bg-red-600 hover:bg-red-700 w-[100px]">Delete</Button>
        </div>
      </ConfirmDialog>

      <SuccessErrorModal
        :show-modal="modal.show"
        :title="modal.title"
        :description="modal.description"
        :is-error="modal.isError"
        @close="modal.show = false"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import ConfirmDialog from '@/components/common/ConfirmDialog.vue';
import LayupBrandedSpinner from '@/components/common/LayupBrandedSpinner.vue';
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import SectionDivider from '@/components/common/SectionDivider.vue';
import SuccessErrorModal from '@/components/common/SuccessErrorModal.vue';
import MasterCardIcon from '@/components/icons/MasterCardIcon.vue';
import VisaCardIcon from '@/components/icons/VisaCardIcon.vue';
import { Button } from '@/components/ui/button';
import { NetworkError } from '@/errors';
import { fetchBanks } from '@/services/banks';
import { deleteCard, getCards, updateCard } from '@/services/cards';
import { updateUserRaw } from '@/services/user';
import { useUserStore } from '@/stores/users';
import { formatNameForAvs } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { useBreakpoint } from '@/utils/useBreakPoint';
import { computed, ref } from 'vue';
import type { ModelCardResponse } from '@layup/api-sdk';
import AddBankDetails from './payment-methods/AddBankDetails.vue';
import ManageCards from './payment-methods/ManageCards.vue';

const userStore = useUserStore();

const { isDesktop } = useBreakpoint();
const { user } = userStore;

const hasIdentity = computed(() => !!user.idNumber || !!user.passportNumber);
const hasBankDetails = ref(false);
const editingBank = ref(false);
const isSaving = ref(false);
const modal = ref({ show: false, title: '', description: '', isError: false });
const loadingCardId = ref<string | null>(null);
const showSelectDefaultDialog = ref(false);
const cardToDelete = ref<string | null>(null);
const showSetDefaultDialog = ref(false);
const showVerifyingDialog = ref(false);
const showDeleteCardDialog = ref(false);
const isLoading = ref(true);

const cards = ref([] as ModelCardResponse[]);
const banks = ref<any[]>([]);
const bankDetailsRef = ref<any>(null);
const newlyAddedCardDetails = ref({} as ModelCardResponse | null);

const bankDetails = ref({
  bank: '',
  accountType: '',
  accountNumber: '',
  branchCode: '',
});

const bankBackup = ref({ ...bankDetails.value });

/**
 * Card details handlers
 * This includes all the functions needed to add, remove, and update users cards
 */
const getUserCards = async () => {
  const response = await getCards();
  cards.value =
    response?.sort((a, b) => (b.defaultCard! ? 1 : 0) - (a.defaultCard! ? 1 : 0)).filter((card) => card.cardDigits) ||
    [];
};

const checkCardBrand = (card: ModelCardResponse) => {
  switch (card.cardType) {
    case 'VISA':
      return {
        icon: VisaCardIcon,
        customDescription: `Visacard ending with ${card.cardDigits}`,
      };
    case 'MASTER':
      return {
        icon: MasterCardIcon,
        customDescription: `Mastercard ending with ${card.cardDigits}`,
      };
    default:
      return {
        icon: null,
        customDescription: null,
      };
  }
};

const onCardAdded = async () => {
  const existingCardIds = cards.value.map((c) => c._id);
  await getUserCards();
  const newestCard = cards.value.find((c) => !existingCardIds.includes(c._id));
  newlyAddedCardDetails.value = newestCard!;
  showSetDefaultDialog.value = true;
};

const setAsDefault = async (cardId: string, closeDialog?: boolean) => {
  loadingCardId.value = cardId;
  try {
    await updateCard({ _id: cardId, defaultCard: true });
    await getUserCards();
    addMetric('update_payment_method', { status: 'success', channel: 'shopper', action: 'set_default_card' });

    if (closeDialog) {
      showSetDefaultDialog.value = false;
      newlyAddedCardDetails.value = null;
    } else {
      const card = cards.value.find((c) => c._id === cardId);
      modal.value = {
        show: true,
        title: 'Success',
        description: `Card ending with ${card?.cardDigits} updated as default card successfully.`,
        isError: false,
      };
    }
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('update_payment_method', { status: 'network_error', channel: 'shopper', action: 'set_default_card' });
    } else {
      createException(error);
      addMetric('update_payment_method', { status: 'error', channel: 'shopper', action: 'set_default_card' });
    }
    modal.value = {
      show: true,
      title: 'Error',
      description: 'Failed to set card as default. Please try again.',
      isError: true,
    };
  } finally {
    loadingCardId.value = null;
  }
};

const skipSetDefault = () => {
  showSetDefaultDialog.value = false;
  newlyAddedCardDetails.value = null;
};

const removeCard = (id: string) => {
  if (cards.value.length === 1) {
    modal.value = {
      show: true,
      title: 'Error Deleting Card',
      description: 'You must have at least one card.',
      isError: true,
    };
    return;
  }

  const card = cards.value.find((c) => c._id === id);
  if (card?.defaultCard && cards.value.length > 1) {
    cardToDelete.value = id;
    showSelectDefaultDialog.value = true;
    return;
  }

  cardToDelete.value = id;
  showDeleteCardDialog.value = true;
};

const confirmDeleteCard = async () => {
  if (!cardToDelete.value) return;

  showDeleteCardDialog.value = false;
  loadingCardId.value = cardToDelete.value;
  try {
    await deleteCard(cardToDelete.value);
    await getUserCards();
    addMetric('update_payment_method', { status: 'success', channel: 'shopper', action: 'delete_card' });
    modal.value = {
      show: true,
      title: 'Success',
      description: 'Card deleted successfully.',
      isError: false,
    };
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('update_payment_method', { status: 'network_error', channel: 'shopper', action: 'delete_card' });
    } else {
      createException(error);
      addMetric('update_payment_method', { status: 'error', channel: 'shopper', action: 'delete_card' });
    }
    modal.value = {
      show: true,
      title: 'Error',
      description: 'Failed to remove card. Please try again.',
      isError: true,
    };
  } finally {
    loadingCardId.value = null;
    cardToDelete.value = null;
  }
};

const setDefaultAndDelete = async (newDefaultId: string) => {
  loadingCardId.value = newDefaultId;

  try {
    await updateCard({ _id: newDefaultId, defaultCard: true });
    await deleteCard(cardToDelete.value!);
    await getUserCards();
    addMetric('update_payment_method', {
      status: 'success',
      channel: 'shopper',
      action: 'set_default_and_delete_card',
    });
    showSelectDefaultDialog.value = false;
    cardToDelete.value = null;
    modal.value = {
      show: true,
      title: 'Success',
      description: 'Card deleted successfully.',
      isError: false,
    };
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('update_payment_method', {
        status: 'network_error',
        channel: 'shopper',
        action: 'set_default_and_delete_card',
      });
    } else {
      createException(error);
      addMetric('update_payment_method', {
        status: 'error',
        channel: 'shopper',
        action: 'set_default_and_delete_card',
      });
    }
    modal.value = {
      show: true,
      title: 'Error',
      description: 'Failed to remove card. Please try again.',
      isError: true,
    };
  } finally {
    loadingCardId.value = null;
  }
};

// Bank details handlers
const addBankDetails = () => {
  hasBankDetails.value = true;
  editingBank.value = true;
};

const handleCancel = () => {
  if (!bankBackup.value.bank) {
    hasBankDetails.value = false;
  }
  editingBank.value = false;
  bankDetails.value = { ...bankBackup.value };
};

const submitBankDetails = async () => {
  if (!bankDetailsRef.value?.validate()) return;

  showVerifyingDialog.value = true;

  try {
    const { initials, surname } = formatNameForAvs(user?.name! || '');
    const id = user?.idNumber || user?.passportNumber;

    const response = await updateUserRaw(user._id!, {
      ...user,
      bankDetails: {
        bankName: { value: bankDetails.value.bank, verified: false },
        account: { value: bankDetails.value.accountNumber, verified: false },
        accountType: { value: bankDetails.value.accountType, verified: false },
        branchCode: { value: bankDetails.value.branchCode, verified: false },
        idNumber: { value: id, verified: false },
        initials: { value: initials, verified: false },
        email: { value: user.email, verified: false },
        clientName: { value: surname, verified: false },
        cellNumber: { value: user.cellNumber, verified: false },
        verified: false,
      },
    });

    userStore.user = response;
    addMetric('update_payment_method', { status: 'success', channel: 'shopper', action: 'update_bank_details' });

    if (response.bankDetails?.verified === false) {
      bankDetailsRef.value?.handleVerificationResponse({ verified: false });
      showVerifyingDialog.value = false;
      return;
    }

    bankDetailsRef.value?.handleVerificationResponse({ verified: true });
    bankBackup.value = { ...bankDetails.value };
    editingBank.value = false;
    showVerifyingDialog.value = false;
    modal.value = {
      show: true,
      title: 'Success',
      description: 'Your banking details have been updated successfully.',
      isError: false,
    };
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('update_payment_method', {
        status: 'network_error',
        channel: 'shopper',
        action: 'update_bank_details',
      });
    } else {
      createException(error);
      addMetric('update_payment_method', { status: 'error', channel: 'shopper', action: 'update_bank_details' });
    }
    showVerifyingDialog.value = false;
    modal.value = {
      show: true,
      title: 'Error',
      description: 'Failed to update banking details. Please try again.',
      isError: true,
    };
  } finally {
    showVerifyingDialog.value = false;
  }
};

const getBanks = async () => {
  const response = await fetchBanks();
  banks.value = response.banks || [];
};

const init = async () => {
  isLoading.value = true;
  await getUserCards();
  await getBanks();

  if (user.bankDetails?.verified) {
    hasBankDetails.value = true;
    bankDetails.value = {
      bank: user.bankDetails.bankName?.value || '',
      accountType: user.bankDetails.accountType?.value || '',
      accountNumber: user.bankDetails.account?.value || '',
      branchCode: user.bankDetails.branchCode?.value || '',
    };

    bankBackup.value = { ...bankDetails.value };
  }
  isLoading.value = false;
};

init();
</script>
